import { toast } from '@/components/toast';
import { maxImageSizeInBytes } from '@/environment';
import { removeMetadataAndCreateFile } from '@/hooks';
import { useGetPresentersList, useRefetchPresentersList } from '@/hooks/presenter';
import { useAnalytics } from '@/services/analytics';
import { uploadImage } from '@/services/api';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { ERROR_KINDS, moderationError } from '@/state/layout/moderation';
import { isImageProcessing } from '@/state/presenters';
import { AppRoutes } from '@/utils/types';
import { bytesToMB, getFileWithSanitizedName, localFileToURL, setImageBackground } from '@/utils/utils';
import { Presenter, PresenterUsecase } from '@d-id/types';
import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { useSetRecoilState } from 'recoil';

export const useUploadAvatarImage = (
    from: string = '',
    usecase: PresenterUsecase = PresenterUsecase.Video,
    onPresenterAdded?: (presenter?: Presenter) => void
) => {
    const mixpanel = useAnalytics();
    const { isAnonymous, loginWithRedirect } = useAuth();
    const setModerationError = useSetRecoilState(moderationError);
    const setIsUploading = useSetRecoilState(isImageProcessing);

    const { refetchPresentersList } = useRefetchPresentersList();
    const { refetch: refetchGetPresentersList } = useGetPresentersList({ usecase, includeNotReady: true });

    const { mutateAsync: mutation } = useMutation(uploadImage, {
        onMutate() {
            setIsUploading(true);
            mixpanel?.track('upload-image', { event: 'click' });
        },
        async onSuccess() {
            const [{ data: newPresenters }] = await Promise.all([refetchGetPresentersList(), refetchPresentersList()]);

            const uploadsPresenters = newPresenters?.filter(p => p.id.startsWith('upl'));
            onPresenterAdded?.(uploadsPresenters?.[0]);

            setIsUploading(false);
            mixpanel?.track('upload-image', { event: 'success' });
        },
        onError: async (error: AxiosError<{ kind: string; description?: string }>, { image }: any) => {
            setIsUploading(false);
            let title = 'Failed to upload image';
            let message = '';
            const kind = error?.response?.data?.kind;
            mixpanel?.track('upload-image', { error: kind, event: 'error' });

            if (kind === ERROR_KINDS.CelebrityDetectedError) {
                return setModerationError({ kind: ERROR_KINDS.CelebrityDetectedError, extraData: await localFileToURL(image) });
            }
            if (kind === ERROR_KINDS.ImageModerationError) {
                return setModerationError({ kind: ERROR_KINDS.ImageModerationError, extraData: await localFileToURL(image) });
            }

            let isInfoToast = false;

            if (kind === ERROR_KINDS.FaceError) {
                message = 'We could not identify a face in the uploaded image. Please use a different image.';
                isInfoToast = true;
            } else if (kind === ERROR_KINDS.InsufficientCreditsError) {
                isInfoToast = true;
                title = `Not enough credits to upload an image.`;
            } else if (error?.response?.data?.description) {
                isInfoToast = true;
                title = `Image Upload failed`;
                message = `${error.response.data.description}`;
            } else {
                logger.error(message, { error });
            }

            const toastMethod = isInfoToast ? toast.info : toast.error;
            toastMethod({ title, message, toastId: `upload-image-error` });
        },
        retry(failureCount: number, error: any) {
            if (failureCount >= 3) {
                return false;
            }
            return !!error.response?.status && (error.response.status === 403 || error.response.status >= 500);
        },
    });

    const onInputFile = async (file: File, onEnd?: () => void) => {
        if (isAnonymous) {
            mixpanel?.track('go-to-signup', { event: 'click', from: 'upload-image', isAnonymous });
            loginWithRedirect({
                appState: { returnTo: window.location.origin + AppRoutes.PostSso + `?from=${from}` },
            });
            onEnd?.();
        } else {
            const { type, size } = file;

            if (!!type && !['image/png', 'image/jpeg', 'image/jpg'].includes(type)) {
                toast.info({ title: 'File type not supported', message: 'Please try uploading png, jpeg or jpg files' });
                onEnd?.();
            } else if (size > maxImageSizeInBytes) {
                toast.info({ title: `Image must be under ${bytesToMB(maxImageSizeInBytes, true)}MB` });
                onEnd?.();
            } else {
                let finalFile = file;
                try {
                    finalFile = await removeMetadataAndCreateFile(file, type);
                    const imageWithBackground = await setImageBackground(finalFile);

                    await mutation({
                        image: getFileWithSanitizedName(imageWithBackground),
                    });
                } catch (error) {
                    logger.error('Failed to upload avatar image', error);
                } finally {
                    onEnd?.();
                }
            }
        }
    };
    const inputEventHandler = async (e: React.MouseEvent<HTMLInputElement>, onEnd?: (e: Event) => void) => {
        if (isAnonymous) {
            mixpanel?.track('go-to-signup', { event: 'click', from: 'upload-image', isAnonymous });
            loginWithRedirect({
                appState: { returnTo: window.location.origin + AppRoutes.PostSso + `?from=${from}` },
            });
            e.preventDefault();
            onEnd?.(e.nativeEvent);
        } else {
            e.currentTarget.onchange = async e => {
                const target = e.target as HTMLInputElement;
                const file = target.files?.[0] ?? null;

                if (file) {
                    const { type, size } = file;

                    if (!!type && !['image/png', 'image/jpeg', 'image/jpg'].includes(type)) {
                        toast.info({ title: `Unsupported file type. Try PNG, JPEG, or JPG` });
                        onEnd?.(e);
                    } else if (size > maxImageSizeInBytes) {
                        toast.info({ title: `Image must be under ${bytesToMB(maxImageSizeInBytes, true)}MB` });
                        onEnd?.(e);
                    } else {
                        let finalFile = file;
                        try {
                            finalFile = await removeMetadataAndCreateFile(file, type);
                        } catch {}
                        try {
                            const imageWithBackground = await setImageBackground(finalFile);

                            await mutation({
                                image: getFileWithSanitizedName(imageWithBackground),
                            });
                        } finally {
                            target.value = '';
                            onEnd?.(e);
                        }
                    }
                }
            };
        }
    };
    return { inputEventHandler, onInputFile };
};
